import { NgClass, NgStyle } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { IGroupedSkeletonLoaderConfig } from './grouped-skeleton-loader.interface';
import { ISkeletonColumn } from './skeleton-loader.interface';

type TSkeletonColumnWithId = ISkeletonColumn & { id: string };

function stableGroupedSkeletonColumnId(prefix: string, column: ISkeletonColumn, index: number): string {
  if (column.id) {
    return column.id;
  }

  return `${prefix}-${index}`;
}

@Component({
  imports: [IconComponent, NgxSkeletonLoaderModule, NgClass, NgStyle],
  selector: 'app-grouped-skeleton-loader',
  styleUrl: './grouped-skeleton-loader.component.scss',
  templateUrl: './grouped-skeleton-loader.component.html',
})
export class GroupedSkeletonLoaderComponent {
  public readonly config = input.required<IGroupedSkeletonLoaderConfig>();

  public readonly childColumnsFormatted = computed((): TSkeletonColumnWithId[] =>
    (this.config().childColumns ?? []).map((column, index) => ({
      ...column,
      id: stableGroupedSkeletonColumnId('grouped-skeleton-child', column, index),
    })),
  );

  public readonly childIndentPx = computed((): number => this.config().childIndentPx ?? 24);

  public readonly childRowIndices = computed((): number[] => {
    const count = this.config().childRowsPerGroup ?? 2;

    return Array.from({ length: count }, (_, index) => index);
  });

  public readonly childBodyRowStyle = computed((): Record<string, string> => {
    const gridCols = this.childRowGridTemplateColumnsResolved();

    if (gridCols) {
      return {
        alignItems: 'center',
        display: 'grid',
        gap: '0',
        'grid-template-columns': gridCols,
      };
    }

    return {
      alignItems: 'center',
      display: 'flex',
      gap: this.columnGapResolved(),
    };
  });

  public readonly childRowGridTemplateColumnsResolved = computed((): string | null => {
    const value = this.config().childRowGridTemplateColumns?.trim();

    if (!value) {
      return null;
    }

    return value;
  });

  public readonly childWrapperClassResolved = computed((): string => {
    const extra = this.config().childWrapperClass?.trim();

    if (!extra) {
      return '';
    }

    return extra;
  });

  public readonly childWrapperMergedStyle = computed((): Record<string, string> => {
    return {
      ...(this.config().childWrapperStyle ?? {}),
      paddingLeft: `${this.childIndentPx()}px`,
    };
  });

  public readonly columnGapResolved = computed((): string => this.config().columnGap ?? '0');

  public readonly containerClassResolved = computed((): string => this.config().containerClass ?? '');

  public readonly defaultColumnPadding = '0 16px' as const;

  public readonly detailModeResolved = computed(
    (): 'collapsed' | 'expanded' => this.config().detailMode ?? 'collapsed',
  );

  public readonly expandAllButtonTheme = { 'border-radius': '100vw', height: '20px', width: '80px' } as const;

  public readonly expandColumnClass = computed((): string => {
    return (
      this.config().expandColumn?.className ?? 'grouped-skeleton-expand-column flex shrink-0 items-center justify-end'
    );
  });

  public readonly expandColumnTheme = computed((): Record<string, string> => {
    const expand = this.config().expandColumn;

    return {
      'border-radius': expand?.skeletonBorderRadius ?? '100vw',
      height: expand?.skeletonHeight ?? '20px',
      width: expand?.skeletonWidth ?? '20px',
    };
  });

  public readonly groupIndices = computed((): number[] => {
    const count = this.config().groupCount ?? 5;

    return Array.from({ length: count }, (_, index) => index);
  });

  public readonly headerColumnTheme = { 'border-radius': '4px', height: '20px', width: '60px' } as const;

  public readonly headerMarginResolved = computed((): string => this.config().headerMargin ?? '0');

  public readonly headerPaddingResolved = computed((): string => this.config().headerPadding ?? '0 16px');

  public readonly iconOnlyTheme = {
    'border-radius': '50%',
    height: '20px',
    width: '32px',
  } as const;

  public readonly iconTheme = {
    'border-radius': '100vw',
    height: '20px',
    width: '32px',
  } as const;

  public readonly paginatorLongTheme = { height: '24px', width: '192px' } as const;

  public readonly paginatorShortTheme = { height: '24px', width: '192px' } as const;

  public readonly parentBodyRowStyle = computed((): Record<string, string> => {
    const gridCols = this.parentGridTemplateColumnsResolved();

    if (gridCols) {
      return {
        alignItems: 'center',
        display: 'grid',
        gap: '0',
        'grid-template-columns': gridCols,
      };
    }

    return {
      alignItems: 'center',
      display: 'flex',
      gap: this.columnGapResolved(),
    };
  });

  public readonly parentColumnsFormatted = computed((): TSkeletonColumnWithId[] =>
    (this.config().parentColumns ?? []).map((column, index) => ({
      ...column,
      id: stableGroupedSkeletonColumnId('grouped-skeleton-parent', column, index),
    })),
  );

  public readonly parentGridTemplateColumnsResolved = computed((): string | null => {
    const value = this.config().parentGridTemplateColumns?.trim();

    if (!value) {
      return null;
    }

    return value;
  });

  public readonly parentHeaderRowStyle = computed((): Record<string, string> => {
    const gridCols = this.parentGridTemplateColumnsResolved();
    const margin = this.headerMarginResolved();

    if (gridCols) {
      return {
        alignItems: 'center',
        display: 'grid',
        gap: '0',
        'grid-template-columns': gridCols,
        margin,
      };
    }

    return {
      alignItems: 'center',
      display: 'flex',
      gap: this.columnGapResolved(),
      margin,
    };
  });

  public readonly rowSeparatorEnabledResolved = computed((): boolean => this.config().rowSeparatorEnabled !== false);

  public readonly searchSkeletonTheme = {
    'border-radius': '12px',
    height: '40px',
    width: '100%',
  } as const;

  public readonly showChildHeaderResolved = computed((): boolean => this.config().showChildHeader === true);

  public readonly showColumnsResolved = computed((): boolean => this.config().showColumns !== false);

  public readonly showPaginatorResolved = computed((): boolean => this.config().showPaginator !== false);

  public readonly tableSurfaceClassResolved = computed((): string => {
    const custom = this.config().tableSurfaceClass?.trim();

    if (custom) {
      return custom;
    }

    return 'table-container simple-hover-table grouped-datatable-table';
  });

  public readonly textTheme = { 'border-radius': '4px', height: '20px', width: '100%' } as const;

  public readonly textWithIconTheme = {
    'border-radius': '4px',
    height: '20px',
    width: '100px',
  } as const;

  public readonly hasAlteringChildRowBackgroundResolved = computed(
    (): boolean => this.config().hasAlteringChildRowBackground !== false,
  );

  public getCol(column: TSkeletonColumnWithId, rowIndex: number): TSkeletonColumnWithId {
    if (column.perRow?.[rowIndex]) {
      return { ...column, ...column.perRow[rowIndex], id: column.id };
    }

    return column;
  }

  public getColumnClass(column: ISkeletonColumn): string {
    if (column.className) {
      return column.className;
    }

    return column.width ? '' : 'flex-1';
  }

  public isLastGroup(groupIndex: number): boolean {
    const indices = this.groupIndices();

    if (indices.length === 0) {
      return true;
    }

    return groupIndex === indices.length - 1;
  }
}
